<template>
    <div class="tms-suggest" :class="$attrs.class">
        <input
            v-bind="$attrs"
            ref="suggestInput"
            v-model="localValue"
            type="text"
            class="suggest-input"
            :class="inputClass"
            @input="handleInput"
            @blur="handleBlur"
            @keydown.down="handleArrowDown"
            @keydown.up="handleArrowUp"
            @keydown.enter="handleEnter"
            @keydown.esc="handleEsc"
            @keydown.tab="handleTab"
        />
        <ul
            v-if="isOpen"
            class="suggestions"
            :class="{
                [suggestionsClass]: true,
                show: isOpen,
            }"
            :aria-labelledby="String($attrs.id)"
            :aria-expanded="isOpen"
        >
            <li
                v-for="(suggestion, si) in suggestions"
                :key="si"
                class="suggestion-item"
                role="button"
                :class="{
                    [suggestionItemClass]: true,
                    [activeClass]: isActive(si),
                }"
                :aria-current="isActive(si)"
                @click="handleItemClick(si)"
            >
                <template v-for="(mt, mti) in markTerms(suggestion, localValue)">
                    <strong v-if="mt.style === 'mark'" :key="mti">{{ mt.value }}</strong>
                    <template v-else>{{ mt.value }}</template>
                </template>
            </li>
        </ul>
    </div>
</template>

<script lang="ts">
import type { PropType } from 'vue';

import { defineComponent } from 'vue';

import { escapeRegExp } from '../utils/stringUtils';

type Style = 'normal' | 'mark';
interface MarkedText {
    style: Style;
    value: string;
}

export default defineComponent({
    name: 'TmsSuggest',
    inheritAttrs: false,
    props: {
        modelValue: {
            type: String,
            default: '',
            required: true,
        },
        suggestions: {
            type: [Array] as PropType<string[]>,
            required: false,
            default: () => [],
        },
        inputClass: {
            type: String,
            default: 'form-control',
        },
        suggestionsClass: {
            type: String,
            default: 'dropdown-menu',
        },
        suggestionItemClass: {
            type: String,
            default: 'dropdown-item',
        },
        activeClass: {
            type: String,
            default: 'active',
        },
        minLength: {
            type: Number,
            default: 0,
        },
    },
    emits: ['input', 'update:modelValue', 'select'],
    data() {
        return {
            userClose: true,
            selectedSuggestionIndex: -1,
        };
    },
    computed: {
        localValue: {
            get(): string {
                return this.modelValue;
            },
            set(val: string) {
                this.$emit('update:modelValue', val);
                this.$emit('input', val);
            },
        },
        isOpen() {
            return !this.userClose && this.localValue.length >= this.minLength && this.suggestions.length > 0;
        },
        selectedSuggestion() {
            return this.selectedSuggestionIndex >= 0 ? this.suggestions[this.selectedSuggestionIndex] : undefined;
        },
    },
    mounted() {
        document.addEventListener('click', this.handleBlur);
    },
    unmounted() {
        document.removeEventListener('click', this.handleBlur);
    },
    methods: {
        selectListItem(val: string) {
            this.localValue = val;
            this.userClose = true;
            this.selectedSuggestionIndex = -1;
            (this.$refs.suggestInput as HTMLInputElement).focus();
            this.$emit('select', val);
        },
        handleItemClick(index: number) {
            this.selectListItem(this.suggestions[index]);
        },
        handleEsc() {
            this.userClose = true;
        },
        handleBlur(event: Event) {
            if (!this.$el.contains(event.target)) {
                this.userClose = true;
            }
        },
        handleTab() {
            if (this.isOpen && this.selectedSuggestion) {
                this.selectListItem(this.selectedSuggestion);
            } else {
                this.userClose = true;
            }
        },
        isActive(val: number) {
            return this.selectedSuggestionIndex === val;
        },
        handleInput() {
            this.selectedSuggestionIndex = -1;
            this.userClose = false;
        },
        handleArrowDown(event: Event) {
            if (this.isOpen) {
                // if the suggestions are open prevent arrow down from moving the cursor
                event.preventDefault();
                if (this.selectedSuggestionIndex < this.suggestions.length - 1) {
                    this.selectedSuggestionIndex += 1;
                }
            }
        },
        handleArrowUp(event: Event) {
            if (this.isOpen) {
                // if the suggestions are open prevent arrow up from moving the cursor
                event.preventDefault();
                if (this.selectedSuggestionIndex > 0) {
                    this.selectedSuggestionIndex -= 1;
                }
            }
        },
        handleEnter(event: Event) {
            if (this.isOpen) {
                // if the suggestions are open prevent form submit on enter
                event.preventDefault();
                if (this.selectedSuggestion) {
                    this.selectListItem(this.selectedSuggestion);
                } else {
                    this.userClose = true;
                }
            }
        },

        markTerms(text: string, term: string): MarkedText[] {
            if (!term.trim()) {
                // fast exit if term is empty
                return [{ style: 'normal', value: text }];
            }

            // ReDoS not an issue as this is run on the client machine
            const regexp = new RegExp(escapeRegExp(term), 'gi');
            const matches = text.matchAll(regexp);

            const result: MarkedText[] = [];
            let curIndex = 0;
            let match = matches.next();
            while (!match.done) {
                const matchStart = match.value.index || 0;
                const matchEnd = matchStart + term.length;
                if (curIndex < matchStart) {
                    result.push({ style: 'normal', value: text.substring(curIndex, matchStart) });
                }
                result.push({ style: 'mark', value: text.substring(matchStart, matchEnd) });
                curIndex = matchEnd;
                match = matches.next();
            }
            if (curIndex < text.length) {
                result.push({ style: 'normal', value: text.substring(curIndex) });
            }
            return result;
        },
    },
});
</script>

<style></style>
